import { encounterCreatureField, inventoryField, itemsCol } from '../shared/array-types.js';
import { latchPullerAllowed, planLatch } from '../janus-engine/actions/latch.js';
import { creatureId, latchedObject, latchPoint, upper } from '../janus-engine/movement/latch-link.js';
import { fixedDragObject } from '../janus-engine/actions/object-drag-facts.js';
import { commitLatchVerdict } from './gm-boundary-truth.js';
import { actorEncounterFor, actorRowFor, hydrateMovementStatuses, movementPermission, movementScopeReaders, waitForActorMovement } from './movement-truth.js';
import { inventoryAttachment, inventoryFootprint, inventoryHeightFt, normalizeTransform } from '../janus-engine/inventory/floor-move.js';
import { footprintInputs, routeLiftWorld } from './inventory-floor-move-truth.js';
import { SIZE_WIDTHS_FT } from '../janus-engine/inventory/lift-occupancy.js';
import { claimFt3dInteractionMode, releaseFt3dInteractionMode } from '../shared/ft3d-interaction-mode.js';
import type { SceneObjectAction } from '../encounter-creation-engine/scene-object-action-menu.js';

type Row = Record<string, unknown>;
const busy = new Set<string>();
let choiceEpoch = 0;
const LATCH_INPUT_WAIT_MESSAGE = 'Latch inputs are still settling. Wait for the earlier change to save, then try again.';
const LATCH_LOADING_MESSAGE = 'The object’s latch state is still loading. Try again shortly.';
function latchScope(encounterId: string, actorId: string, objectId?: string) {
  const encounter = actorEncounterFor(encounterId) as Row | null, actor = actorRowFor(encounterId, actorId) as Row | null;
  if (!encounter || !actor) return null;
  const object = (encounter.encounter_inventories_arr as Row[] | undefined)?.find(row => upper(inventoryField(row, 'inventory_id')) === upper(objectId)) ?? null;
  const userId = upper(window.FateTide?.user?.id);
  const read = { ...movementScopeReaders(encounter), controls: (row: Row) =>
    userId !== '' && upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_created_by_id')) === userId };
  let attached = false;
  if (object && !fixedDragObject(object, read)) {
    attached = normalizeTransform(inventoryField(object, 'transform_3d'))?.mounted === true || read.hasStatus(object, 'integrated_object');
    if (!attached) {
      const input = footprintInputs(encounter, object), at = latchPoint(object), world = routeLiftWorld(encounter).rest;
      attached = inventoryAttachment(world, { id: upper(objectId), mounted: false, integrated: false,
        footprintAt: (x, y) => inventoryFootprint(input, id => SIZE_WIDTHS_FT[id] ?? null, { x, y }),
        height: inventoryHeightFt(input, id => SIZE_WIDTHS_FT[id] ?? null) }, at.x, at.y, at.z) !== null;
    }
  }
  return { encounter, actor, object, read, attached };
}
/** Join held-input adoption and both movement lanes under one bound; parked inputs release the action lock. */
async function latchInputs(encounterId: string, actorId: string, pullerId: string): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([Promise.all([hydrateMovementStatuses(encounterId), waitForActorMovement(encounterId, actorId),
      ...(pullerId !== actorId ? [waitForActorMovement(encounterId, pullerId)] : [])]),
    new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(LATCH_INPUT_WAIT_MESSAGE)), 30000); })]);
  } finally { clearTimeout(timer); }
}
export async function commitLatch(encounterId: string, actorId: string, objectId?: string, pullerId = actorId, release = !objectId): Promise<boolean> {
  const key = `${encounterId}:${actorId}`;
  if (busy.has(key)) return false;
  busy.add(key);
  try {
    if (!movementPermission(encounterId, actorId).allowed) { window.displayWarning?.('You cannot act for this creature.'); return false; }
    await latchInputs(encounterId, actorId, pullerId);
    const scope = latchScope(encounterId, actorId, objectId), puller = actorRowFor(encounterId, pullerId) as Row | null;
    if (!scope || !puller || !movementPermission(encounterId, actorId).allowed) { window.displayWarning?.('Load the actor and puller in this encounter first.'); return false; }
    const { encounter, actor, object, read, attached } = scope;
    if (objectId && !object) { window.displayWarning?.('Choose an object on this encounter’s floor.'); return false; }
    const plan = planLatch(encounter, actor, object, release, read, () => crypto.randomUUID().toUpperCase(), attached, puller);
    if (plan.reason !== null) { window.displayWarning?.(plan.reason); return false; }
    const result = await commitLatchVerdict(encounterId, actorId, plan.verdict,
      Number(encounterCreatureField(actor.encounter_creatures, 'movement_rev') ?? 0));
    return result === 'handled';
  } catch (error) {
    console.warn('[latch] change unavailable', error);
    window.displayWarning?.(error instanceof Error && error.message === LATCH_INPUT_WAIT_MESSAGE ? LATCH_INPUT_WAIT_MESSAGE
      : 'The object’s latch state is not ready. Try again shortly.');
    return false;
  } finally { busy.delete(key); }
}

/** Contextual bar action: only release an existing self latch. Objects are chosen by their radial. */
export function chooseLatch(actorId: string): void {
  const actor = window.FateTide?.casterCache?.get(actorId) as Row | undefined;
  const encounterId = upper(encounterCreatureField(actor?.encounter_creatures, 'encounter_id'));
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!actor || !encounter) { window.displayWarning?.('Load the creature’s encounter first.'); return; }
  if (!latchedObject(actor)) return;
  (document.getElementById('ft-latch-chooser') as HTMLDialogElement | null)?.close();
  const epoch = ++choiceEpoch;
  claimFt3dInteractionMode('latch-targeting', () => {});
  void commitLatch(encounterId, creatureId(actor)).finally(() => { if (epoch === choiceEpoch) releaseFt3dInteractionMode('latch-targeting'); });
}

function pullerPlans(scope: NonNullable<ReturnType<typeof latchScope>>, release: boolean) {
  const { encounter, actor, object, read, attached } = scope;
  return (encounter.encounter_creatures_arr as Row[] ?? []).filter(row => latchPullerAllowed(actor, row, read))
    .map(row => ({ row, plan: planLatch(encounter, actor, object, release, read, () => 'preview', attached, row) }));
}

/** An object-scoped puller choice. Only eligible held creatures appear; no ambient creature target state. */
function choosePuller(encounterId: string, actorId: string, objectId: string, release: boolean): void {
  if (upper(window.ftGetActiveCasterId?.()) !== actorId) return;
  let scope: ReturnType<typeof latchScope>;
  try { scope = latchScope(encounterId, actorId, objectId); }
  catch { window.displayWarning?.(LATCH_LOADING_MESSAGE); return; }
  const choices = scope ? pullerPlans(scope, release).filter(choice => choice.plan.reason === null) : [];
  if (!choices.length) { window.displayWarning?.('No eligible puller is ready. Move within reach of the object and a friendly puller, then try again.'); return; }
  (document.getElementById('ft-latch-chooser') as HTMLDialogElement | null)?.close();
  const epoch = ++choiceEpoch;
  const retire = () => { if (epoch === choiceEpoch) releaseFt3dInteractionMode('latch-targeting'); };
  let dialog: HTMLDialogElement | null = null, confirmed = false;
  claimFt3dInteractionMode('latch-targeting', () => { dialog?.close(); retire(); });
  const commit = (pullerId: string): void => {
    if (upper(window.ftGetActiveCasterId?.()) !== actorId) { retire(); return; }
    void commitLatch(encounterId, actorId, objectId, pullerId, release).finally(retire);
  };
  if (choices.length === 1 && creatureId(choices[0]!.row) === actorId) { commit(actorId); return; }
  dialog = document.createElement('dialog'); dialog.id = 'ft-latch-chooser';
  const chooser = dialog;
  const heading = document.createElement('h3'); heading.textContent = release ? 'Unlatch which puller?' : 'Choose a puller'; dialog.append(heading);
  for (const { row } of choices) {
    const button = document.createElement('button'); button.type = 'button'; button.className = 'btn btn-dark m-1';
    const id = creatureId(row);
    button.textContent = id === actorId ? 'Yourself' : String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_name') || itemsCol(row, 'item_name') || 'Puller');
    button.dataset.ftLatchPuller = id;
    button.addEventListener('click', () => { confirmed = true; chooser.close(); commit(id); });
    dialog.append(button);
  }
  const cancel = document.createElement('button'); cancel.type = 'button'; cancel.className = 'btn btn-secondary m-1'; cancel.textContent = 'Cancel';
  cancel.addEventListener('click', () => chooser.close()); dialog.append(cancel);
  dialog.addEventListener('close', () => { chooser.remove(); if (!confirmed) retire(); }, { once: true }); document.body.append(dialog); dialog.showModal();
}

function readyLatchRadialActions(objectId: string): SceneObjectAction[] {
  const actorId = upper(window.ftGetActiveCasterId?.());
  const actor = window.FateTide?.casterCache?.get(actorId) as Row | undefined;
  const encounterId = upper(encounterCreatureField(actor?.encounter_creatures, 'encounter_id'));
  const scope = latchScope(encounterId, actorId, objectId);
  if (!scope?.object || !movementPermission(encounterId, actorId).allowed) return [];
  const actions: SceneObjectAction[] = [];
  for (const release of [false, true]) {
    if (!release && fixedDragObject(scope.object, scope.read, scope.attached)) continue;
    const plans = pullerPlans(scope, release).filter(choice => !release || latchedObject(choice.row) === upper(objectId));
    const available = plans.some(choice => choice.plan.reason === null);
    if (release && !plans.length) continue;
    actions.push({ label: release ? 'Unlatch' : 'Latch', icon: 'grapple', enabled: available,
      reason: available ? null : plans.find(choice => creatureId(choice.row) === actorId)?.plan.reason ?? plans[0]?.plan.reason ?? 'Move within reach of the object and a friendly puller first.',
      run: () => choosePuller(encounterId, actorId, objectId, release) });
  }
  return actions;
}
export function latchRadialActions(objectId: string): SceneObjectAction[] {
  try { return readyLatchRadialActions(objectId); }
  catch { return [{ label: 'Latch', icon: 'grapple', enabled: false,
    reason: LATCH_LOADING_MESSAGE, run: () => {} }]; }
}
declare global { interface Window { ftCommitLatch?: typeof commitLatch; ftCommitLatchVerdict?: typeof commitLatchVerdict; ftLatchRadialActions?: typeof latchRadialActions } }
Object.assign(window, { ftLatchChoice: chooseLatch, ftCommitLatch: commitLatch, ftCommitLatchVerdict: commitLatchVerdict, ftLatchRadialActions: latchRadialActions });
